/**
 * APA 7 title case, for headings, table titles and figure titles.
 *
 * APA's rules (Publication Manual, 7th ed., §6.17; as stated on the APA Style site and
 * quoted by university writing centres):
 * - capitalize the first word of a title or heading, the first word of a subtitle,
 *   and the first word after a colon, em dash or end punctuation — even a minor word;
 * - capitalize major words (nouns, verbs including linking verbs, adjectives, adverbs,
 *   pronouns) and every word of four letters or more ("With", "Between", "From");
 * - capitalize both parts of a hyphenated major word ("Self-Report", not
 *   "Self-report");
 * - lowercase only the minor words of three letters or fewer: short conjunctions
 *   (and, as, but, for, if, nor, or, so, yet), articles (a, an, the) and short
 *   prepositions (as, at, by, for, in, of, off, on, per, to, up, via).
 *
 * APA has no "always capitalize the last word" rule — that is Chicago's — so a minor
 * word at the end stays lowercase.
 *
 * Without a part-of-speech tagger, every short word that is not on the minor list is
 * treated as major ("Is", "Its", "How", "Not"), which is what APA's examples do.
 * Words typed with internal capitals or in capitals (DNA, iPhone, COVID-19) keep them;
 * a heading typed entirely in capitals is lowercased first, so its acronyms need
 * restoring by hand.
 */

const MINOR_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "as",
  "but",
  "for",
  "if",
  "nor",
  "or",
  "so",
  "yet",
  "at",
  "by",
  "in",
  "of",
  "off",
  "on",
  "per",
  "to",
  "up",
  "via",
  "vs",
  "vs.",
  "v.",
]);

/** Words, the spaces between them, and em dashes written without spaces. */
const RE_SPACE_SPLIT = /(\s+|—)/;
const RE_TRAILING_PUNCT = /[,;"”’)]+$/u;
const RE_HAS_LOWER = /\p{Ll}/u;
const RE_HAS_LETTER = /\p{L}/u;
const RE_INTERNAL_CAPITAL = /^.\S*\p{Lu}/u;
const RE_ALL_CAPS = /^[^\p{Ll}]*\p{Lu}[^\p{Ll}]*$/u;
const RE_LEADING = /^([^\p{L}\p{N}]*)(.*)$/u;
const RE_SENTENCE_BREAK = /(?:[:.?!—]|--)["”’)]*$/;
const RE_HYPHEN = /(-)/;

function capitalizeFirst(word: string): string {
  const [, lead, rest] = RE_LEADING.exec(word) ?? ["", "", word];
  return lead + rest.charAt(0).toLocaleUpperCase("en") + rest.slice(1);
}

/** A word the writer capitalized on purpose: an acronym or a brand like "iPhone". */
function keepsOwnCase(word: string): boolean {
  const letters = RE_LEADING.exec(word)?.[2] ?? word;
  return (
    (RE_ALL_CAPS.test(letters) && letters.length > 1) ||
    RE_INTERNAL_CAPITAL.test(letters)
  );
}

function bareWord(word: string): string {
  return word
    .replace(RE_LEADING, "$2")
    .toLowerCase()
    .replace(RE_TRAILING_PUNCT, "");
}

function caseWord(word: string, forceCapital: boolean): string {
  if (!RE_HAS_LETTER.test(word) || keepsOwnCase(word)) {
    return word;
  }
  const lower = word.toLocaleLowerCase("en");
  if (!forceCapital && MINOR_WORDS.has(bareWord(word))) {
    return lower;
  }
  // "self-report" → "Self-Report"; a minor part after a hyphen stays lowercase
  // ("Follow-up"), as in APA's rule for hyphenated major words.
  return lower
    .split(RE_HYPHEN)
    .map((part, index) => {
      if (part === "-" || part === "") {
        return part;
      }
      return index === 0 || !MINOR_WORDS.has(part)
        ? capitalizeFirst(part)
        : part;
    })
    .join("");
}

const RE_ENGLISH_LOCALE = /^en(?:-|$)/i;

/**
 * Whether APA's English title-case rules apply to text in this locale. A missing locale
 * means English, the tool's default; "en" and "en-GB" are English, "de" and "ent" are not.
 */
export function isEnglishLocale(locale: string | undefined): boolean {
  return locale === undefined || RE_ENGLISH_LOCALE.test(locale);
}

/** Convert a heading or title to APA title case. */
export function apaTitleCase(text: string): string {
  const source = RE_HAS_LOWER.test(text) ? text : text.toLocaleLowerCase("en");
  const tokens = source.trim().split(RE_SPACE_SPLIT);
  let first = true;
  return tokens
    .map((token) => {
      if (token.trim() === "") {
        return token;
      }
      if (token === "—") {
        first = true;
        return token;
      }
      const cased = caseWord(token, first);
      if (RE_HAS_LETTER.test(token)) {
        first = false;
      }
      if (RE_SENTENCE_BREAK.test(token)) {
        first = true;
      }
      return cased;
    })
    .join("");
}
